import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Bootstrap Modal型定義
declare global {
  interface Window {
    bootstrap?: {
      Modal?: {
        getInstance: (element: Element) => { hide: () => void } | null;
      };
    };
  }
}

interface LoginModalProps {
  id?: string;
}

/**
 * モダンでおしゃれなログインモーダル
 * ミニマルデザインとユーザビリティを重視
 */
const LoginModal: React.FC<LoginModalProps> = ({ id = 'loginModal' }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // フォーム入力処理
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // エラーをクリア
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // ログイン処理
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setErrors({
        general: 'ユーザー名とパスワードを入力してください'
      });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
  
      const success = await login(formData);
  
      
      if (success) {
        // ログイン成功時のみ

        
        // フォームリセット
        setFormData({ username: '', password: '' });
        setErrors({});
        
        // モーダルを閉じる（より確実な方法）
        const modal = document.getElementById(id);
        if (modal) {
          // Bootstrap Modal インスタンスを取得して閉じる
          const modalInstance = window.bootstrap?.Modal?.getInstance(modal);
          if (modalInstance) {
            modalInstance.hide();
          } else {
            // フォールバック: 直接閉じる
            modal.style.display = 'none';
            modal.classList.remove('show');
            document.body.classList.remove('modal-open');
            const backdrop = document.querySelector('.modal-backdrop');
            if (backdrop) {
              backdrop.remove();
            }
          }
        }
        
                 // 認証状態の更新を待ってからマイページに遷移
         setTimeout(() => {
           navigate('/mypage');
           // 強制的にページ全体の再レンダリングを促す
           window.dispatchEvent(new Event('auth-state-changed'));
         }, 200);
      } else {
        // ログイン失敗時はエラーメッセージを表示
        setErrors({ 
          general: 'ユーザー名またはパスワードが正しくありません' 
        });
      }
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Login error:', error);
      }
      setErrors({ 
        general: error instanceof Error ? error.message : 'ログインに失敗しました' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal fade" id={id} tabIndex={-1} aria-labelledby={`${id}Label`} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content modern-auth-modal">
          
          {/* ヘッダー */}
          <div className="modern-auth-header">
            <div className="auth-logo">
              <img 
                src="/img/splice_logo.png" 
                alt="SPLICE" 
                className="logo-image"
              />
            </div>
            <button
              type="button"
              className="modern-close-btn"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* コンテンツ */}
          <div className="modern-auth-body">
            <div className="auth-title">
              <h2>ログイン</h2>
            </div>

            {loading ? (
              <div className="auth-loading">
                <div className="loading-spinner"></div>
                <span>ログイン中...</span>
              </div>
            ) : (
              <form onSubmit={handleLogin} className="auth-form">
                {/* エラーメッセージ */}
                {errors.general && (
                  <div className="auth-error">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1.33333L14.6667 14H1.33333L8 1.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 6V8.66667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 11.3333H8.00667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {errors.general}
                  </div>
                )}

                {/* ユーザー名 */}
                <div className="form-group">
                  <label htmlFor="username">ユーザー名</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M15 15.75V14.25C15 13.4544 14.6839 12.6913 14.1213 12.1287C13.5587 11.5661 12.7956 11.25 12 11.25H6C5.20435 11.25 4.44129 11.5661 3.87868 12.1287C3.31607 12.6913 3 13.4544 3 14.25V15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 8.25C10.6569 8.25 12 6.90685 12 5.25C12 3.59315 10.6569 2.25 9 2.25C7.34315 2.25 6 3.59315 6 5.25C6 6.90685 7.34315 8.25 9 8.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      placeholder="ユーザー名を入力"
                      className={errors.username ? 'error' : ''}
                      autoComplete="username"
                    />
                  </div>
                  {errors.username && <span className="field-error">{errors.username}</span>}
                </div>

                {/* パスワード */}
                <div className="form-group">
                  <label htmlFor="password">パスワード</label>
                  <div className="input-wrapper">
                    <svg className="input-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <rect x="3.75" y="7.5" width="10.5" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6.75 7.5V5.25C6.75 4.45435 7.06607 3.69129 7.62868 3.12868C8.19129 2.56607 8.95435 2.25 9.75 2.25C10.5456 2.25 11.3087 2.56607 11.8713 3.12868C12.4339 3.69129 12.75 4.45435 12.75 5.25V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="パスワードを入力"
                      className={errors.password ? 'error' : ''}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path d="M10.73 10.73C10.5225 10.9465 10.2768 11.1191 10.0066 11.2377C9.7364 11.3563 9.44674 11.4187 9.15381 11.4211C8.86089 11.4235 8.57037 11.3658 8.29845 11.2518C8.02653 11.1378 7.77854 10.9696 7.56761 10.7567C7.35668 10.5438 7.19047 10.2918 7.0785 10.0179C6.96653 9.74405 6.91075 9.45328 6.91514 9.16036C6.91954 8.86744 6.98404 8.57834 7.10459 8.31011C7.22514 8.04188 7.39959 7.79813 7.618 7.594L10.73 10.73ZM13.347 13.347C12.0512 14.4137 10.4521 15.0016 8.8 15.0016C7.14792 15.0016 5.54879 14.4137 4.253 13.347C2.25 11.625 2.25 6.375 4.253 4.653C5.54879 3.58632 7.14792 2.99844 8.8 2.99844C10.4521 2.99844 12.0512 3.58632 13.347 4.653C15.35 6.375 15.35 11.625 13.347 13.347Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M2.25 2.25L15.75 15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                          <path d="M1.5 9C1.5 9 4.5 3 9 3C13.5 3 16.5 9 16.5 9C16.5 9 13.5 15 9 15C4.5 15 1.5 9 1.5 9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9 11.25C10.2426 11.25 11.25 10.2426 11.25 9C11.25 7.75736 10.2426 6.75 9 6.75C7.75736 6.75 6.75 7.75736 6.75 9C6.75 10.2426 7.75736 11.25 9 11.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <span className="field-error">{errors.password}</span>}
                </div>

                {/* ログインボタン */}
                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  <span>ログイン</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {/* 新規登録 */}
                <div className="auth-switch">
                  <span>アカウントをお持ちでない方</span>
                  <button
                    type="button"
                    className="auth-switch-btn"
                    data-bs-dismiss="modal"
                    data-bs-toggle="modal"
                    data-bs-target="#registerModal"
                  >
                    新規アカウント作成
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal; 